import React, { useState, useEffect } from 'react';
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Eye,
  EyeOff,
  AlertCircle,
  Building,
  Phone,
  Mail,
  MapPin,
  Users
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Client } from '../../types/client';
import { PaginatedResponse } from '../../types/common';
import { User } from '../../types/auth';
import { clientService, userService } from '../../services';
import { useAuth } from '../../hooks/useAuth';
import { formatDate } from '../../utils/dateUtils';
import { ClientBulkAssignModal } from './ClientBulkAssignModal';

interface ClientListProps {
  refreshTrigger?: number;
  onCreateClient: () => void;
  onEditClient: (client: Client) => void;
}

export const ClientList: React.FC<ClientListProps> = ({
  refreshTrigger = 0,
  onCreateClient,
  onEditClient
}) => {
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  // Initialize from URL params lazily to avoid a render-then-effect race
  // (mirrors AccountList's pattern from Round 8).
  const [noAccountsFilter, setNoAccountsFilter] = useState<boolean>(() => {
    return searchParams.get('no_accounts') === '1';
  });
  // R18 Task I (Peter): "list of the clients under a specific sales guy".
  // Lazy initialiser for the same render-then-effect reason as above.
  const [salesmanFilter, setSalesmanFilter] = useState<number | 'all'>(() => {
    const id = searchParams.get('user_id');
    return id ? parseInt(id, 10) : 'all';
  });
  const [salesmen, setSalesmen] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(25);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedClientForBulkAssign, setSelectedClientForBulkAssign] = useState<Client | null>(null);

  // Statistics state
  const [stats, setStats] = useState({
    totalClients: 0,
    totalAccounts: 0,
    activeAccounts: 0,
    avgAccountsPerClient: 0
  });

  const loadClients = async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page,
        per_page: perPage,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(noAccountsFilter && { no_accounts: '1' }),
        ...(salesmanFilter !== 'all' && { user_id: salesmanFilter })
      };

      const response: PaginatedResponse<Client> = await clientService.getClients(params);
      setClients(response.data);
      setTotalPages(response.meta.last_page);
      setCurrentPage(response.meta.current_page);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats for all clients (separate from pagination)
  const fetchStats = async () => {
    try {
      // Fetch with high per_page to get accurate aggregate counts.
      // Forward `no_accounts` so the count tiles match the filtered list
      // (Round 8 lesson — stats are computed client-side from this fetch).
      const statsParams = {
        per_page: 10000, // High number to get all records with counts
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(noAccountsFilter && { no_accounts: '1' }),
        ...(salesmanFilter !== 'all' && { user_id: salesmanFilter })
      };

      const statsResponse: PaginatedResponse<Client> = await clientService.getClients(statsParams);
      const allClients = statsResponse.data;
      const total = statsResponse.meta.total;

      // Calculate aggregate stats from the returned data
      const totalAccounts = allClients.reduce((sum, client) => sum + (client.accounts_count || 0), 0);
      const activeAccounts = allClients.reduce((sum, client) => sum + (client.active_accounts_count || 0), 0);

      setStats({
        totalClients: total,
        totalAccounts: totalAccounts,
        activeAccounts: activeAccounts,
        avgAccountsPerClient: total > 0 ? Math.round(totalAccounts / total) : 0
      });
    } catch (err: any) {
      console.error('Failed to fetch stats:', err);
    }
  };

  // Salesman list for the filter dropdown — loaded once.
  useEffect(() => {
    userService
      .getUsers({ per_page: 200 })
      .then((res) => setSalesmen(res.data))
      .catch(() => setSalesmen([]));
  }, []);

  // Keep ?user_id= in the URL so a filtered list can be bookmarked/shared.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (salesmanFilter === 'all') {
      next.delete('user_id');
    } else {
      next.set('user_id', String(salesmanFilter));
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesmanFilter]);

  // Fetch clients when page changes or parent triggers refresh
  useEffect(() => {
    loadClients(currentPage);
  }, [currentPage, searchTerm, statusFilter, noAccountsFilter, salesmanFilter, refreshTrigger]);

  // Fetch stats only when filters change or parent triggers refresh
  useEffect(() => {
    fetchStats();
  }, [searchTerm, statusFilter, noAccountsFilter, salesmanFilter, refreshTrigger]);

  const handleToggleStatus = async (client: Client) => {
    if (actionLoading === client.id) return;

    try {
      setActionLoading(client.id);
      await clientService.toggleClientStatus(client.id);
      setSuccessMessage(`Client ${client.is_active ? 'deactivated' : 'activated'} successfully`);
      await loadClients(currentPage);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update client status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (client: Client): Promise<void> => {
    const accountCount = client.accounts_count ?? client.accounts?.length ?? 0;
    let force = false;

    if (accountCount > 0) {
      const forceConfirm = window.confirm(
        `"${client.client_name}" has ${accountCount} account(s) with assigned employees.\n\n` +
        `Do you want to unassign all employees, remove all accounts, and delete this client?`
      );
      if (!forceConfirm) return;
      force = true;
    } else {
      if (!window.confirm(`Are you sure you want to delete "${client.client_name}"?`)) {
        return;
      }
    }

    try {
      setActionLoading(client.id);
      await clientService.deleteClient(client.id, force);
      setSuccessMessage('Client deleted successfully');
      await loadClients(currentPage);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete client');
      setActionLoading(null);
    }
  };

  const handleBulkAssignSuccess = () => {
    setSuccessMessage('Employees assigned successfully');
    loadClients(currentPage);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const canManageClient = () => {
    return currentUser?.role === 'super_admin' || currentUser?.role === 'admin';
  };

  if (loading && clients.length === 0) {
    return (
      <div className="card">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-gray-200 rounded" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-200 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-3">
            <Building className="h-6 w-6 sm:h-8 sm:w-8 text-primary-600 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Client Management</h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                Manage client information and account relationships
              </p>
            </div>
          </div>

          {canManageClient() && (
            <button
              onClick={onCreateClient}
              className="btn btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto"
            >
              <Plus className="w-4 h-4" />
              <span>New Client</span>
            </button>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      {!loading && stats.totalClients > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Total Clients</p>
                <p className="text-2xl font-bold text-blue-900">
                  {stats.totalClients}
                </p>
                <p className="text-xs text-blue-600 mt-1">Across all pages</p>
              </div>
              <Building className="h-8 w-8 text-blue-600 opacity-75" />
            </div>
          </div>

          <div className="card bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Total Accounts</p>
                <p className="text-2xl font-bold text-green-900">
                  {stats.totalAccounts}
                </p>
                <p className="text-xs text-green-600 mt-1">All client accounts</p>
              </div>
              <ToggleRight className="h-8 w-8 text-green-600 opacity-75" />
            </div>
          </div>

          <div className="card bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Active Accounts</p>
                <p className="text-2xl font-bold text-purple-900">
                  {stats.activeAccounts}
                </p>
                <p className="text-xs text-purple-600 mt-1">Currently active</p>
              </div>
              <Eye className="h-8 w-8 text-purple-600 opacity-75" />
            </div>
          </div>

          <div className="card bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">Avg per Client</p>
                <p className="text-2xl font-bold text-orange-900">
                  {stats.avgAccountsPerClient}
                </p>
                <p className="text-xs text-orange-600 mt-1">Average accounts</p>
              </div>
              <Filter className="h-8 w-8 text-orange-600 opacity-75" />
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-2">
          <div className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center">
            <div className="w-2 h-2 bg-white rounded-full"></div>
          </div>
          <span className="text-green-800">{successMessage}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Filters */}
      <div className="card space-y-3">
        {/* R18: three filters now — search, status, salesman. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search clients by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none text-sm"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          {/* R18 Task I (Peter): "list of the clients under a specific sales
              guy". Traverses Client -> Account -> UserAccount server-side. */}
          <div className="relative">
            <label htmlFor="client-salesman" className="sr-only">Salesman</label>
            <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              id="client-salesman"
              value={salesmanFilter}
              onChange={(e) => {
                setCurrentPage(1);
                setSalesmanFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10));
              }}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none text-sm"
            >
              <option value="all">All Salesmen</option>
              {salesmen.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Discoverability toggle — mirrors AccountList "No employees" filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setNoAccountsFilter(!noAccountsFilter)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
              noAccountsFilter
                ? 'bg-orange-50 border-orange-300 text-orange-800 hover:bg-orange-100'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            title="Show clients that have no accounts assigned"
          >
            <Building className={`h-4 w-4 ${noAccountsFilter ? 'text-orange-600' : 'text-gray-400'}`} />
            {noAccountsFilter ? 'Showing No-Accounts Only' : 'Show No-Accounts Clients'}
          </button>
        </div>
      </div>

      {/* Clients Table */}
      <div className="card overflow-hidden">
        {clients.length === 0 && !loading ? (
          <div className="text-center py-12">
            <Building className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'No clients found' : 'No clients yet'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm
                ? 'Try adjusting your search criteria'
                : 'Create your first client to get started'
              }
            </p>
            {!searchTerm && canManageClient() && (
              <button
                onClick={onCreateClient}
                className="btn btn-primary"
              >
                Create First Client
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client Information
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Accounts
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {clients.map((client) => (
                    <tr key={client.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {client.client_name}
                          </div>
                          {client.description && (
                            <div className="text-sm text-gray-500 mt-1 max-w-xs truncate">
                              {client.description}
                            </div>
                          )}
                          <div className="text-xs text-gray-400 mt-1">
                            Created {formatDate(client.created_at)}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {client.contact_email && (
                            <div className="flex items-center text-sm text-gray-600">
                              <Mail className="h-3 w-3 mr-2" />
                              {client.contact_email}
                            </div>
                          )}
                          {client.phone && (
                            <div className="flex items-center text-sm text-gray-600">
                              <Phone className="h-3 w-3 mr-2" />
                              {client.phone}
                            </div>
                          )}
                          {client.address && (
                            <div className="flex items-center text-sm text-gray-600">
                              <MapPin className="h-3 w-3 mr-2" />
                              <span className="max-w-xs truncate">{client.address}</span>
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <a
                          href={`/accounts?client_id=${client.id}`}
                          className="text-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            window.location.href = `/accounts?client_id=${client.id}`;
                          }}
                        >
                          <div className="font-medium">
                            {client.accounts_count || 0} accounts
                          </div>
                          <div className="text-xs text-gray-500">
                            {client.active_accounts_count || 0} active
                          </div>
                        </a>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        {canManageClient() ? (
                          <button
                            onClick={() => handleToggleStatus(client)}
                            disabled={actionLoading === client.id}
                            className={`
                              flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium transition-colors
                              ${client.is_active
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-red-100 text-red-800 hover:bg-red-200'
                              }
                            `}
                          >
                            {actionLoading === client.id ? (
                              <div className="animate-spin h-3 w-3 border border-current border-t-transparent rounded-full"></div>
                            ) : client.is_active ? (
                              <>
                                <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                                <span>Active</span>
                              </>
                            ) : (
                              <>
                                <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                                <span>Inactive</span>
                              </>
                            )}
                          </button>
                        ) : (
                          <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium
                            ${client.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                          `}>
                            <div className={`w-2 h-2 rounded-full ${client.is_active ? 'bg-green-600' : 'bg-red-600'}`}></div>
                            <span>{client.is_active ? 'Active' : 'Inactive'}</span>
                          </span>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {canManageClient() && (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setSelectedClientForBulkAssign(client)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Bulk Assign Employees to All Accounts"
                            >
                              <Users className="h-4 w-4" />
                            </button>

                            <button
                              onClick={() => onEditClient(client)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Edit Client"
                            >
                              <Edit className="h-4 w-4" />
                            </button>

                            <button
                              onClick={() => handleDelete(client)}
                              disabled={actionLoading === client.id}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Client"
                            >
                              {actionLoading === client.id ? (
                                <div className="animate-spin h-4 w-4 border border-current border-t-transparent rounded-full"></div>
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden divide-y divide-gray-200">
              {clients.map((client) => (
                <div key={client.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900">{client.client_name}</h3>
                      {client.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{client.description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        Created {formatDate(client.created_at)}
                      </p>
                    </div>
                    <div className="ml-3 flex-shrink-0">
                      {canManageClient() ? (
                        <button
                          onClick={() => handleToggleStatus(client)}
                          disabled={actionLoading === client.id}
                          className={`
                            flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium transition-colors
                            ${client.is_active
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                            }
                          `}
                        >
                          {actionLoading === client.id ? (
                            <div className="animate-spin h-3 w-3 border border-current border-t-transparent rounded-full"></div>
                          ) : (
                            <>
                              <div className={`w-2 h-2 rounded-full ${client.is_active ? 'bg-green-600' : 'bg-red-600'}`}></div>
                              <span>{client.is_active ? 'Active' : 'Inactive'}</span>
                            </>
                          )}
                        </button>
                      ) : (
                        <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium
                          ${client.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                        `}>
                          <div className={`w-2 h-2 rounded-full ${client.is_active ? 'bg-green-600' : 'bg-red-600'}`}></div>
                          <span>{client.is_active ? 'Active' : 'Inactive'}</span>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-1">
                    {client.contact_email && (
                      <div className="flex items-center text-xs text-gray-600">
                        <Mail className="h-3 w-3 mr-2 flex-shrink-0" />
                        <span className="truncate">{client.contact_email}</span>
                      </div>
                    )}
                    {client.phone && (
                      <div className="flex items-center text-xs text-gray-600">
                        <Phone className="h-3 w-3 mr-2 flex-shrink-0" />
                        <span>{client.phone}</span>
                      </div>
                    )}
                    {client.address && (
                      <div className="flex items-center text-xs text-gray-600">
                        <MapPin className="h-3 w-3 mr-2 flex-shrink-0" />
                        <span className="truncate">{client.address}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <div className="text-xs text-gray-500">
                      {client.accounts?.length || 0} accounts ({client.accounts?.filter(acc => acc.is_active).length || 0} active)
                    </div>

                    {canManageClient() && (
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => setSelectedClientForBulkAssign(client)}
                          className="flex items-center space-x-1 text-blue-600 hover:text-blue-900 text-xs"
                          title="Bulk Assign"
                        >
                          <Users className="h-3 w-3" />
                          <span>Assign</span>
                        </button>
                        <button
                          onClick={() => onEditClient(client)}
                          className="flex items-center space-x-1 text-indigo-600 hover:text-indigo-900 text-xs"
                        >
                          <Edit className="h-3 w-3" />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => handleDelete(client)}
                          disabled={actionLoading === client.id}
                          className="flex items-center space-x-1 text-red-600 hover:text-red-900 text-xs"
                        >
                          {actionLoading === client.id ? (
                            <div className="animate-spin h-3 w-3 border border-current border-t-transparent rounded-full"></div>
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                          <span>Delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Assign Modal */}
      {selectedClientForBulkAssign && (
        <ClientBulkAssignModal
          client={selectedClientForBulkAssign}
          onClose={() => setSelectedClientForBulkAssign(null)}
          onSuccess={handleBulkAssignSuccess}
        />
      )}
    </div>
  );
};